import { Controller } from "@hotwired/stimulus"
import { jsonRequest } from "../lib/hudini/csrf.js"

// The effort picker in the composer: Low / Medium / High. Catalog providers
// map each tier to a model and (where supported) a reasoning budget — OpenAI
// and Grok keep one model and vary effort; Claude steps Haiku → Sonnet → Opus.
// Switching persists immediately as the user's default, so a reload before the
// first message still opens on that choice. The `hudini` controller also reads
// the current tier on send, so the turn itself uses whatever is selected.
//
// Rendered only for providers with a model catalog, so every method no-ops
// safely when the picker isn't on the page.
export default class extends Controller {
  static targets = ["picker", "trigger", "triggerLabel", "menu", "option"]
  static values = { tier: String, url: String }

  connect() {
    this.menuOpen = false
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
  }

  // Read by the `hudini` controller when building the send payload. Null when
  // no picker is rendered, so those accounts send no tier at all.
  get selectedTier() {
    return this.hasPickerTarget && this.hasTierValue ? this.tierValue : null
  }

  toggleMenu(event) {
    event.preventDefault()
    if (this.menuOpen) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  openMenu() {
    if (!this.hasMenuTarget) return

    // Only one composer menu at a time so the two upward menus never fight
    // for clicks / stacking context.
    this.closeSkillsMenu()

    this.menuOpen = true
    this.menuTarget.classList.remove("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.add("hudini__skill-trigger--open")
  }

  closeSkillsMenu() {
    const skills = this.application.getControllerForElementAndIdentifier(this.element, "hudini-skills")
    if (skills) skills.closeSkillMenu()
  }

  closeMenu() {
    if (!this.hasMenuTarget) return

    this.menuOpen = false
    this.menuTarget.classList.add("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove("hudini__skill-trigger--open")
  }

  closeOnOutsideClick(event) {
    if (!this.menuOpen) return
    if (this.hasPickerTarget && this.pickerTarget.contains(event.target)) return
    this.closeMenu()
  }

  select(event) {
    event.preventDefault()
    const { tier, label } = event.currentTarget.dataset
    if (!tier) return

    const changed = this.tierValue !== tier
    this.tierValue = tier
    if (this.hasTriggerLabelTarget) this.triggerLabelTarget.textContent = label
    this.syncOptions()
    this.closeMenu()
    if (changed) this.persistTier(tier)
  }

  // Fire-and-forget: a failed save still leaves the picker on the new choice,
  // and the next send carries the tier for that turn.
  persistTier(tier) {
    if (!this.hasUrlValue) return

    fetch(this.urlValue, jsonRequest("PATCH", { model_tier: tier })).catch(() => {})
  }

  syncOptions() {
    this.optionTargets.forEach(option => {
      option.classList.toggle("hudini__model-option--selected", option.dataset.tier === this.tierValue)
    })
  }

  // Clicking a row must not pull focus out of the contenteditable composer.
  keepFocus(event) {
    event.preventDefault()
  }
}
